import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useBrandRoute } from '@/routes/brandRoute'

import type {
  BrandDetailsResponse,
  MetaLinkApiError,
  LinkBrandResponse,
  CheckLinkStatusResponse,
  VerifyViewAccessRequest,
  VerifyViewAccessResponse,
} from './types'
import {
  getBrandDetailsApi,
  linkBrandApi,
  checkLinkStatusApi,
  verifyViewAccessApi,
} from './helpers'
import { retryUnlessAccessDenied } from '../../utils/helper.utils'

export function useFetchBrandDetails() {
  const routeParams = useBrandRoute()
  const brandId = routeParams.brandId ?? null
  const platform = routeParams.platform ?? null

  const brandIdNumber = brandId ? Number.parseInt(brandId, 10) : null
  const isEnabled = Boolean(brandIdNumber && platform)

  const {
    data,
    isLoading,
    error,
    isSuccess,
    refetch: fetchBrandDetails,
  } = useQuery<BrandDetailsResponse, MetaLinkApiError>({
    queryKey: ['brandDetails', brandIdNumber, platform],
    queryFn: async () => {
      if (!brandIdNumber) {
        throw new Error('Brand ID not available in URL params.')
      }
      if (!platform) {
        throw new Error('Platform not available in URL params.')
      }
      return getBrandDetailsApi(brandIdNumber, platform)
    },
    enabled: isEnabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: retryUnlessAccessDenied,
  })

  return {
    fetchBrandDetails,
    fetchBrandDetailsAsync: async () => {
      const result = await fetchBrandDetails()
      if (result?.data) {
        return result.data
      }
      throw new Error('Failed to fetch brand details')
    },
    brandDetailsData: data,
    isLoading,
    error,
    isSuccess,
  }
}

export function useLinkBrand() {
  const routeParams = useBrandRoute()
  const queryClient = useQueryClient()
  const brandId = routeParams.brandId ?? null
  const platform = routeParams.platform ?? null

  const brandIdNumber = brandId ? Number.parseInt(brandId, 10) : null

  const {
    mutate: linkBrand,
    mutateAsync: linkBrandAsync,
    isPending: isLinkingBrand,
    error,
    isSuccess,
    data,
    reset: resetLinkBrand,
  } = useMutation<LinkBrandResponse, MetaLinkApiError, string>({
    mutationFn: async (businessId: string) => {
      if (!brandIdNumber) {
        throw new Error('Brand ID not available in URL params.')
      }
      if (!platform) {
        throw new Error('Platform not available in URL params.')
      }
      return linkBrandApi(brandIdNumber, platform, businessId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brandDetails', brandIdNumber, platform] })
    },
  })

  return {
    linkBrand,
    linkBrandAsync,
    isLinkingBrand,
    error,
    isSuccess,
    data,
    resetLinkBrand,
  }
}

export function useCheckLinkStatus() {
  const routeParams = useBrandRoute()
  const queryClient = useQueryClient()
  const brandId = routeParams.brandId ?? null
  const platform = routeParams.platform ?? null

  const brandIdNumber = brandId ? Number.parseInt(brandId, 10) : null

  const {
    mutate: checkLinkStatus,
    mutateAsync: checkLinkStatusAsync,
    isPending: isCheckingStatus,
    error,
    isSuccess,
    data,
  } = useMutation<CheckLinkStatusResponse, MetaLinkApiError, void>({
    mutationFn: async () => {
      if (!brandIdNumber) {
        throw new Error('Brand ID not available in URL params.')
      }
      if (!platform) {
        throw new Error('Platform not available in URL params.')
      }
      return checkLinkStatusApi(brandIdNumber, platform)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brandDetails', brandIdNumber, platform] })
    },
  })

  return {
    checkLinkStatus,
    checkLinkStatusAsync,
    isCheckingStatus,
    error,
    isSuccess,
    data,
  }
}

export function useVerifyViewAccess() {
  const routeParams = useBrandRoute()
  const queryClient = useQueryClient()
  const brandId = routeParams.brandId ?? null
  const platform = routeParams.platform ?? null

  const brandIdNumber = brandId ? Number.parseInt(brandId, 10) : null

  const {
    mutate: verifyViewAccess,
    mutateAsync: verifyViewAccessAsync,
    isPending: isVerifyingViewAccess,
    error,
    isSuccess,
    data,
    reset,
  } = useMutation<VerifyViewAccessResponse, MetaLinkApiError, VerifyViewAccessRequest>({
    mutationFn: async (payload: VerifyViewAccessRequest) => {
      if (!brandIdNumber) {
        throw new Error('Brand ID not available in URL params.')
      }
      if (!platform) {
        throw new Error('Platform not available in URL params.')
      }
      return verifyViewAccessApi(brandIdNumber, platform, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brandDetails', brandIdNumber, platform] })
    },
  })

  return {
    verifyViewAccess,
    verifyViewAccessAsync,
    isVerifyingViewAccess,
    error,
    isSuccess,
    data,
    reset,
  }
}
